import React from 'react'
import { CircularProgress } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useNumberFormat } from '../../hooks/useNumberFormat'
import { formatTableDate } from '../../utils/formatDate'
import { AsyncOptionsStatus } from '../../hooks/useAsyncOptions'
import { Plan } from '../../types/planTypes'
import { MissionBudgetInfo, ProjectBudgetInfo } from './parentInfo'
import './QuickCreate.css'

type Item = {
  label: string
  value: React.ReactNode
  tone?: 'warn' | 'danger'
  money?: boolean
}

function SummaryShell({
  kind,
  name,
  items,
  status,
  note,
  plain = false,
}: {
  kind: string
  name?: string
  items: Item[]
  status: AsyncOptionsStatus
  note?: string
  plain?: boolean
}) {
  const { t } = useTranslation()
  return (
    <section
      className={plain ? 'qc-summary qc-summary--plain' : 'qc-summary'}
      aria-label={kind}
      aria-busy={status === 'loading'}
    >
      <header>
        <span className="qc-summary-kind">{kind}</span>
        {name ? <strong title={name}>{name}</strong> : null}
      </header>
      {status === 'loading' ? (
        <p className="qc-summary-loading" role="status">
          <CircularProgress size={16} sx={{ color: '#076B5C' }} aria-hidden />
          <span>{t('loading')}</span>
        </p>
      ) : status === 'error' ? (
        <p className="qc-summary-muted">{t('quickCreate.summary.unavailable')}</p>
      ) : (
        <dl>
          {items.map((item) => (
            <div key={item.label} className={item.tone ? `is-${item.tone}` : undefined}>
              <dt>{item.label}</dt>
              <dd className={item.money ? 'incyia-num' : undefined}>{item.value}</dd>
            </div>
          ))}
        </dl>
      )}
      {status === 'ready' && note ? <p className="qc-summary-note">{note}</p> : null}
    </section>
  )
}

export function MissionSummaryCard({
  info,
  status,
  plain = false,
}: {
  info: MissionBudgetInfo | null
  status: AsyncOptionsStatus
  plain?: boolean
}) {
  const { t, i18n } = useTranslation()
  const fmt = useNumberFormat()
  return (
    <SummaryShell
      kind={t('quickCreate.entity.mission')}
      name={info?.mission.MissionName}
      status={status}
      plain={plain}
      note={plain ? undefined : t('quickCreate.summary.missionNoDeadline')}
      items={
        info
          ? [
              { label: t('quickCreate.summary.budget'), value: fmt.money(info.budget), money: true },
              { label: t('quickCreate.summary.allocated'), value: fmt.money(info.allocated), money: true },
              {
                label: t('quickCreate.summary.remaining'),
                value: fmt.money(info.remaining),
                money: true,
                tone: info.remaining <= 0 ? 'danger' : info.remaining < info.budget * 0.1 ? 'warn' : undefined,
              },
              {
                label: t('quickCreate.summary.created'),
                value: formatTableDate(info.createdAt, i18n.language),
              },
            ]
          : []
      }
    />
  )
}

export function ProjectSummaryCard({
  info,
  status,
  plain = false,
}: {
  info: ProjectBudgetInfo | null
  status: AsyncOptionsStatus
  plain?: boolean
}) {
  const { t, i18n } = useTranslation()
  const fmt = useNumberFormat()
  return (
    <SummaryShell
      kind={t('quickCreate.entity.project')}
      name={info?.projet.ProjetName}
      status={status}
      plain={plain}
      items={
        info
          ? [
              { label: t('quickCreate.summary.budget'), value: fmt.money(info.budget), money: true },
              { label: t('quickCreate.summary.spent'), value: fmt.money(info.spent), money: true },
              {
                label: t('quickCreate.summary.remaining'),
                value: fmt.money(info.remaining),
                money: true,
                tone: info.remaining <= 0 ? 'danger' : info.remaining < info.budget * 0.1 ? 'warn' : undefined,
              },
              {
                label: t('quickCreate.summary.dates'),
                value: `${formatTableDate(info.projet.DateDebut, i18n.language)} → ${formatTableDate(info.projet.DateFin, i18n.language)}`,
              },
            ]
          : []
      }
    />
  )
}

export function PlanSummaryCard({
  plan,
  status,
  plain = false,
}: {
  plan: Plan | null
  status: AsyncOptionsStatus
  plain?: boolean
}) {
  const { t, i18n } = useTranslation()
  return (
    <SummaryShell
      kind={t('quickCreate.entity.plan')}
      name={plan?.PlanName}
      status={status}
      plain={plain}
      items={
        plan
          ? [
              {
                label: t('quickCreate.summary.dates'),
                value: `${formatTableDate(plan.DateDebut, i18n.language)} → ${formatTableDate(plan.DateFin, i18n.language)}`,
              },
            ]
          : []
      }
    />
  )
}
